import React from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/Button';
import { LazyImage } from '@/components/ui/LazyImage';

export function ICFExplainerPage() {
  return (
    <main className="pt-32 pb-20">
      <Helmet>
        <title>ICF CCE Points | Enneagram Certification for Coaches | FIVE LENS</title>
        <meta name="description" content="Earn 23.75 ICF Continuing Coach Education points with FIVE LENS Practitioner Certification. Recognised by the International Coaching Federation." />
        <meta property="og:title" content="ICF CCE Points | Enneagram Certification for Coaches | FIVE LENS" />
        <meta property="og:description" content="Earn 23.75 ICF Continuing Coach Education points with FIVE LENS Practitioner Certification. Recognised by the International Coaching Federation." />
        <meta name="twitter:title" content="ICF CCE Points | Enneagram Certification for Coaches | FIVE LENS" />
        <meta name="twitter:description" content="Earn 23.75 ICF Continuing Coach Education points with FIVE LENS Practitioner Certification. Recognised by the International Coaching Federation." />
        <link rel="canonical" href="https://www.fivelensenneagram.com/icf-cce-points" />
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-8">
        <Breadcrumb items={[{ label: 'For Coaches and Practitioners', href: '/practitioners' }, { label: 'ICF Guide' }]} />
      </div>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <span className="inline-block py-1 px-3 rounded-full bg-rule-3/20 text-rule-3 text-xs font-bold uppercase tracking-wider mb-6">
              Designed for coaches, HR, L&D and OD professionals
            </span>
            <h1 className="text-4xl lg:text-5xl font-serif text-ink leading-tight mb-6">
              Use FIVE LENS with confidence while understanding the ICF landscape.
            </h1>
            <p className="text-lg text-ink/70/80 mb-8 max-w-2xl leading-relaxed">
              FIVE LENS positions the Enneagram as a multidimensional behavioural framework built for coaches and people-development professionals, with a focus on transformation, team effectiveness and culture change. This guide helps visitors understand where that value fits alongside ICF education, credentials and continuing professional development.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/" className="inline-flex items-center justify-center bg-rule-3 hover:bg-rule-3 text-white rounded-full px-8 py-3 font-semibold transition-colors">
                Visit FIVE LENS
              </Link>
              <a href="#why-fivelens" className="inline-flex items-center justify-center bg-transparent border border-ink/70/20 hover:bg-ground-alt text-ink rounded-full px-8 py-3 font-semibold transition-colors">
                See the FIVE LENS difference
              </a>
            </div>

            <div className="mt-12 flex flex-col sm:flex-row items-center gap-8 sm:gap-12 pt-8 border-t border-ground-deep/40">
              <div className="flex flex-wrap items-center gap-8 sm:gap-12">
                <LazyImage 
                  src="/icf-logo.webp" 
                  alt="International Coaching Federation (ICF)" 
                  className="h-24 md:h-28 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity" 
                />
                <LazyImage 
                  src="/cce-logo.webp" 
                  alt="Continuing Coach Education (CCE)" 
                  className="h-[180px] w-auto object-contain opacity-90 hover:opacity-100 transition-opacity" 
                />
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-5">
            <div className="bg-ground-alt border border-ground-deep/50 rounded-2xl p-8 shadow-sm">
              <h3 id="hero-card-title" className="text-xl font-serif text-ink mb-6">
                Why this matters for prospective clients
              </h3>
              <ul className="space-y-4">
                <li className="flex gap-4 items-start text-ink/70/80">
                  <div className="w-2.5 h-2.5 rounded-full bg-rule-3 mt-2 shrink-0 ring-4 ring-rule-3/20"></div>
                  <span>FIVE LENS describes itself as a psychometric framework built for coaches as well as HR, L&D and OD professionals.</span>
                </li>
                <li className="flex gap-4 items-start text-ink/70/80">
                  <div className="w-2.5 h-2.5 rounded-full bg-rule-3 mt-2 shrink-0 ring-4 ring-rule-3/20"></div>
                  <span>The site positions the framework as going beyond surface personality categories to reveal deeper drivers of behaviour and strengthen resilience and lasting change.</span>
                </li>
                <li className="flex gap-4 items-start text-ink/70/80">
                  <div className="w-2.5 h-2.5 rounded-full bg-rule-3 mt-2 shrink-0 ring-4 ring-rule-3/20"></div>
                  <span>This page shows how to explain ICF concepts while still promoting FIVE LENS as a practical tool for coaching, team effectiveness and culture work.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why FIVE LENS */}
      <section id="why-fivelens" className="max-w-7xl mx-auto px-6 lg:px-8 mb-24">
        <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-10">
          <div>
            <span className="text-xs uppercase tracking-wider text-ink/70/60 font-bold block mb-3">Why FIVE LENS</span>
            <h2 className="text-3xl font-serif text-ink">A stronger story for your brand</h2>
          </div>
          <p className="max-w-xl text-ink/70/80 md:text-right">
            FIVE LENS says its model was built to expand self-awareness, strengthen leadership impact and help people work together more meaningfully. It also presents its team and culture work as practical, behaviour-shifting interventions rather than surface-level theory.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-ground-alt border border-ground-deep/40 p-8 rounded-xl shadow-sm">
            <span className="text-xs uppercase tracking-wider text-ink/70/50 font-bold block mb-4">Coaching</span>
            <h3 className="text-xl font-serif text-ink mb-3">More depth for coaches</h3>
            <p className="text-ink/70/80">The website frames FIVE LENS as a coaching-oriented psychometric model that gives practitioners richer behavioural insight and more real-world applicability than a basic personality label.</p>
          </div>
          <div className="bg-ground-alt border border-ground-deep/40 p-8 rounded-xl shadow-sm">
            <span className="text-xs uppercase tracking-wider text-ink/70/50 font-bold block mb-4">Teams</span>
            <h3 className="text-xl font-serif text-ink mb-3">Better team effectiveness</h3>
            <p className="text-ink/70/80">FIVE LENS presents its team framework as a way to tackle trust, communication and collaboration through hands-on interventions rooted in behavioural insight.</p>
          </div>
          <div className="bg-ground-alt border border-ground-deep/40 p-8 rounded-xl shadow-sm">
            <span className="text-xs uppercase tracking-wider text-ink/70/50 font-bold block mb-4">Culture</span>
            <h3 className="text-xl font-serif text-ink mb-3">Culture that changes behaviour</h3>
            <p className="text-ink/70/80">The site says culture initiatives often fail when they chase slogans instead of habits, and positions FIVE LENS as a method for helping executive teams anchor values in everyday behaviour.</p>
          </div>
          <div className="md:col-span-2 lg:col-span-3 bg-ground-alt border border-ground-deep/40 p-8 rounded-xl shadow-sm">
            <span className="text-xs uppercase tracking-wider text-ink/70/50 font-bold block mb-4">How to frame it</span>
            <h3 className="text-xl font-serif text-ink mb-3">Suggested positioning</h3>
            <p className="text-ink/70/80">FIVE LENS is not just about understanding type. It is about giving coaches and organisations a practical framework for deeper insight, more effective conversations, stronger teams and sustainable culture change. That makes it an attractive complement to an ICF-aligned coaching journey.</p>
          </div>
        </div>
      </section>

      {/* ICF Basics */}
      <section id="icf-basics" className="max-w-7xl mx-auto px-6 lg:px-8 mb-24">
        <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-10">
          <div>
            <span className="text-xs uppercase tracking-wider text-ink/70/60 font-bold block mb-3">ICF basics</span>
            <h2 className="text-3xl font-serif text-ink">Explain the ICF world simply</h2>
          </div>
          <p className="max-w-xl text-ink/70/80 md:text-right">
            Prospective learners often confuse an accredited training pathway, a personal credential and ongoing continuing education. The clearest explanation is to show that these are three related but separate pieces.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="bg-ground-alt border border-ground-deep/40 p-8 rounded-xl shadow-sm flex flex-col">
            <span className="text-xs uppercase tracking-wider text-ink/70/50 font-bold block mb-4">Programmes</span>
            <h3 className="text-xl font-serif text-ink mb-3">ICF can accredit training</h3>
            <p className="text-ink/70/80 mb-8 flex-grow">This refers to the education provider or learning pathway. Examples include Level 1, Level 2 and Level 3 pathways used in the current ICF education structure.</p>
            <div className="grid gap-3">
              <div className="bg-white border border-ground-deep p-4 rounded-lg">
                <strong className="block text-ink mb-1">Level 1</strong>
                <span className="text-sm text-ink/70/80">Usually aligned to ACC preparation.</span>
              </div>
              <div className="bg-white border border-ground-deep p-4 rounded-lg">
                <strong className="block text-ink mb-1">Level 2</strong>
                <span className="text-sm text-ink/70/80">Usually aligned to PCC preparation.</span>
              </div>
              <div className="bg-white border border-ground-deep p-4 rounded-lg">
                <strong className="block text-ink mb-1">Level 3</strong>
                <span className="text-sm text-ink/70/80">Advanced preparation toward MCC.</span>
              </div>
            </div>
          </div>

          <div className="bg-ground-alt border border-ground-deep/40 p-8 rounded-xl shadow-sm flex flex-col">
            <span className="text-xs uppercase tracking-wider text-ink/70/50 font-bold block mb-4">Individuals</span>
            <h3 className="text-xl font-serif text-ink mb-3">ICF credentials coaches</h3>
            <p className="text-ink/70/80 mb-8 flex-grow">These personal credentials are ACC, PCC and MCC. They relate to the coach, not only to the course they completed.</p>
            <div className="grid gap-3">
              <div className="bg-white border border-ground-deep p-4 rounded-lg">
                <strong className="block text-ink mb-1">ACC</strong>
                <span className="text-sm text-ink/70/80">Professional foundation.</span>
              </div>
              <div className="bg-white border border-ground-deep p-4 rounded-lg">
                <strong className="block text-ink mb-1">PCC</strong>
                <span className="text-sm text-ink/70/80">Established practitioner.</span>
              </div>
              <div className="bg-white border border-ground-deep p-4 rounded-lg">
                <strong className="block text-ink mb-1">MCC</strong>
                <span className="text-sm text-ink/70/80">Master-level standard.</span>
              </div>
            </div>
          </div>

          <div className="bg-ground-alt border border-ground-deep/40 p-8 rounded-xl shadow-sm flex flex-col">
            <span className="text-xs uppercase tracking-wider text-ink/70/50 font-bold block mb-4">Renewal</span>
            <h3 className="text-xl font-serif text-ink mb-3">CCEUs keep learning active</h3>
            <p className="text-ink/70/80 mb-8 flex-grow">CCEUs are continuing coach education units. They are commonly used after a credential has been earned to support renewal and ongoing professional growth.</p>
            <div className="grid gap-3">
              <div className="bg-white border border-ground-deep p-4 rounded-lg">
                <strong className="block text-ink mb-1">40</strong>
                <span className="text-sm text-ink/70/80">Typical total renewal hours.</span>
              </div>
              <div className="bg-white border border-ground-deep p-4 rounded-lg">
                <strong className="block text-ink mb-1">24</strong>
                <span className="text-sm text-ink/70/80">Typical core competency hours.</span>
              </div>
              <div className="bg-white border border-ground-deep p-4 rounded-lg">
                <strong className="block text-ink mb-1">3</strong>
                <span className="text-sm text-ink/70/80">Typical ethics hours inside renewal.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Table */}
      <section id="credentials" className="max-w-7xl mx-auto px-6 lg:px-8 mb-24">
        <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-10">
          <div>
            <span className="text-xs uppercase tracking-wider text-ink/70/60 font-bold block mb-3">Credentials</span>
            <h2 className="text-3xl font-serif text-ink">Where FIVE LENS fits on the coach journey</h2>
          </div>
          <p className="max-w-xl text-ink/70/80 md:text-right">
            The cleanest brand message is that FIVE LENS strengthens coaching practice and professional impact, while ICF credentials remain a separate formal pathway with their own requirements.
          </p>
        </div>

        <div className="overflow-x-auto bg-ground-alt border border-ground-deep/40 rounded-xl shadow-sm">
          <table className="w-full min-w-[760px] text-left border-collapse">
            <thead>
              <tr className="bg-ground-deep/20 border-b border-ground-deep/40">
                <th className="p-6 text-xs uppercase tracking-wider text-ink/70/70 font-bold">Stage</th>
                <th className="p-6 text-xs uppercase tracking-wider text-ink/70/70 font-bold">What the coach is building</th>
                <th className="p-6 text-xs uppercase tracking-wider text-ink/70/70 font-bold">How FIVE LENS can help</th>
                <th className="p-6 text-xs uppercase tracking-wider text-ink/70/70 font-bold">Important compliance note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ground-deep/20">
              <tr>
                <td className="p-6 text-ink font-semibold">Before ACC</td>
                <td className="p-6 text-ink/70/80">Confidence, structured conversations, better self-awareness and client insight</td>
                <td className="p-6 text-ink/70/80">FIVE LENS can enrich the coach’s behavioural understanding and give more depth to development conversations.</td>
                <td className="p-6 text-ink/70/80">It should not be described as automatically replacing ICF training requirements.</td>
              </tr>
              <tr>
                <td className="p-6 text-ink font-semibold">Building toward PCC</td>
                <td className="p-6 text-ink/70/80">More consistent coaching skill, broader client work, stronger professional identity</td>
                <td className="p-6 text-ink/70/80">FIVE LENS can support richer pattern recognition and more nuanced work with leaders, teams and organisations.</td>
                <td className="p-6 text-ink/70/80">ICF still assesses personal education, hours, mentor coaching and exam requirements separately.</td>
              </tr>
              <tr>
                <td className="p-6 text-ink font-semibold">Mature practice</td>
                <td className="p-6 text-ink/70/80">Integrated methodology, sophisticated client work and wider systems impact</td>
                <td className="p-6 text-ink/70/80">FIVE LENS aligns well with coaches working across leadership, team effectiveness and culture change.</td>
                <td className="p-6 text-ink/70/80">Formal credential claims must match the actual ICF status of the programme or coach.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Path Section */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-24">
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 bg-ground-alt border border-ground-deep/40 p-8 rounded-xl shadow-sm">
            <span className="text-xs uppercase tracking-wider text-ink/70/50 font-bold block mb-6">Recommended message architecture</span>
            <div className="space-y-6">
              <div className="flex gap-6 items-start bg-white p-6 rounded-lg border border-ground-deep/20">
                <div className="w-10 h-10 shrink-0 rounded-full bg-rule-3 text-white flex items-center justify-center font-bold">1</div>
                <div>
                  <strong className="block text-ink mb-2">Lead with business value.</strong>
                  <span className="text-ink/70/80">Position FIVE LENS as a multidimensional framework that helps individuals, teams and organisations create authentic transformation and lasting behaviour change.</span>
                </div>
              </div>
              <div className="flex gap-6 items-start bg-white p-6 rounded-lg border border-ground-deep/20">
                <div className="w-10 h-10 shrink-0 rounded-full bg-rule-3 text-white flex items-center justify-center font-bold">2</div>
                <div>
                  <strong className="block text-ink mb-2">Then explain the ICF distinction.</strong>
                  <span className="text-ink/70/80">Clarify that ICF has separate standards for accredited education, personal coach credentials and continuing education.</span>
                </div>
              </div>
              <div className="flex gap-6 items-start bg-white p-6 rounded-lg border border-ground-deep/20">
                <div className="w-10 h-10 shrink-0 rounded-full bg-rule-3 text-white flex items-center justify-center font-bold">3</div>
                <div>
                  <strong className="block text-ink mb-2">Use accurate confidence language.</strong>
                  <span className="text-ink/70/80">Say that FIVE LENS complements coaching development, strengthens practice and may align well with coaches seeking recognised professional growth, while avoiding claims you cannot substantiate.</span>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-4 bg-ground-alt border border-ground-deep/40 p-8 rounded-xl shadow-sm">
            <span className="text-xs uppercase tracking-wider text-ink/70/50 font-bold block mb-4">Social proof</span>
            <h3 className="text-xl font-serif text-ink mb-3">What your site already signals</h3>
            <p className="text-ink/70/80">The website highlights client praise describing FIVE LENS as world-class, highly practical and a valuable addition to coaching practice, which supports a premium positioning story.</p>
          </div>
        </div>
      </section>

      {/* CCEUs */}
      <section id="cceus" className="max-w-7xl mx-auto px-6 lg:px-8 mb-24">
        <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-10">
          <div>
            <span className="text-xs uppercase tracking-wider text-ink/70/60 font-bold block mb-3">CCEUs</span>
            <h2 className="text-3xl font-serif text-ink">Use this section to answer the question quickly</h2>
          </div>
          <p className="max-w-xl text-ink/70/80 md:text-right">
            Most visitors do not want technical language. They want to know whether a course is a first qualification, a renewal activity or a professional-development add-on.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <div className="bg-ground-alt border border-ground-deep/40 p-8 rounded-xl shadow-sm">
            <h3 className="text-xl font-serif text-ink mb-3">Plain-language answer</h3>
            <p className="text-ink/70/80">CCEUs are learning credits for coaches who want to maintain and deepen their professional development after initial training.</p>
          </div>
          <div className="bg-ground-alt border border-ground-deep/40 p-8 rounded-xl shadow-sm">
            <h3 className="text-xl font-serif text-ink mb-3">Best marketing use</h3>
            <p className="text-ink/70/80">Use CCEU language only where a specific programme has formal approval, and make the exact number of units and category clear.</p>
          </div>
          <div className="bg-ground-alt border border-ground-deep/40 p-8 rounded-xl shadow-sm">
            <h3 className="text-xl font-serif text-ink mb-3">Why it matters</h3>
            <p className="text-ink/70/80">For a coach comparing options, CCEUs signal ongoing credibility and professional growth, not simply another generic workshop.</p>
          </div>
        </div>
        <div className="bg-ground-alt border border-ground-deep/40 p-8 rounded-xl shadow-sm">
          <span className="text-xs uppercase tracking-wider text-ink/70/50 font-bold block mb-4">Helpful positioning line</span>
          <h3 className="text-xl font-serif text-ink mb-3">Suggested copy</h3>
          <p className="text-ink/70/80">FIVE LENS equips coaches and people-development professionals with a powerful behavioural framework for deeper transformation. Where relevant and formally approved, selected learning experiences may also contribute to recognised continuing coach education.</p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-6 lg:px-8 mb-24">
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-wider text-ink/70/60 font-bold block mb-3">Questions</span>
          <h2 className="text-3xl font-serif text-ink mb-4">Answers visitors usually need before they buy</h2>
          <p className="text-ink/70/80">These FAQs keep the tone promotional but credible, which is especially important when professional training and credentials are involved.</p>
        </div>

        <div className="space-y-4">
          <details className="group bg-ground-alt border border-ground-deep/40 p-6 rounded-xl shadow-sm [&_summary::-webkit-details-marker]:hidden">
            <summary className="flex items-center justify-between cursor-pointer font-semibold text-ink list-none text-lg">
              Is FIVE LENS only for accredited coaches?
              <span className="transition group-open:rotate-180 text-rule-3">
                <svg fill="none" height="24" shapeRendering="geometricPrecision" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24" width="24"><path d="M6 9l6 6 6-6"></path></svg>
              </span>
            </summary>
            <p className="text-ink/70/80 mt-4 leading-relaxed">No. The FIVE LENS website says the model is designed for coaches as well as HR, L&D and OD professionals, making it broader than a credential-only audience.</p>
          </details>
          <details className="group bg-ground-alt border border-ground-deep/40 p-6 rounded-xl shadow-sm [&_summary::-webkit-details-marker]:hidden">
            <summary className="flex items-center justify-between cursor-pointer font-semibold text-ink list-none text-lg">
              Can FIVE LENS strengthen an existing coaching practice?
              <span className="transition group-open:rotate-180 text-rule-3">
                <svg fill="none" height="24" shapeRendering="geometricPrecision" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24" width="24"><path d="M6 9l6 6 6-6"></path></svg>
              </span>
            </summary>
            <p className="text-ink/70/80 mt-4 leading-relaxed">Yes. The current site positions the framework as a practical way to deepen insight, improve coaching effectiveness and help clients move beyond surface-level behavioural labels.</p>
          </details>
          <details className="group bg-ground-alt border border-ground-deep/40 p-6 rounded-xl shadow-sm [&_summary::-webkit-details-marker]:hidden">
            <summary className="flex items-center justify-between cursor-pointer font-semibold text-ink list-none text-lg">
              Does FIVE LENS itself automatically equal an ICF credential?
              <span className="transition group-open:rotate-180 text-rule-3">
                <svg fill="none" height="24" shapeRendering="geometricPrecision" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24" width="24"><path d="M6 9l6 6 6-6"></path></svg>
              </span>
            </summary>
            <p className="text-ink/70/80 mt-4 leading-relaxed">No. ICF credentials are awarded through ICF’s own requirements for education, coaching experience, mentor coaching, competence review and exams.</p>
          </details>
          <details className="group bg-ground-alt border border-ground-deep/40 p-6 rounded-xl shadow-sm [&_summary::-webkit-details-marker]:hidden">
            <summary className="flex items-center justify-between cursor-pointer font-semibold text-ink list-none text-lg">
              Why include ICF information on a FIVE LENS page?
              <span className="transition group-open:rotate-180 text-rule-3">
                <svg fill="none" height="24" shapeRendering="geometricPrecision" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24" width="24"><path d="M6 9l6 6 6-6"></path></svg>
              </span>
            </summary>
            <p className="text-ink/70/80 mt-4 leading-relaxed">Because many serious buyers want to understand how a methodology, a credential pathway and continuing education relate to one another before they invest.</p>
          </details>
          <details className="group bg-ground-alt border border-ground-deep/40 p-6 rounded-xl shadow-sm [&_summary::-webkit-details-marker]:hidden">
            <summary className="flex items-center justify-between cursor-pointer font-semibold text-ink list-none text-lg">
              How does FIVE LENS help with my CCEU requirements?
              <span className="transition group-open:rotate-180 text-rule-3">
                <svg fill="none" height="24" shapeRendering="geometricPrecision" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24" width="24"><path d="M6 9l6 6 6-6"></path></svg>
              </span>
            </summary>
            <p className="text-ink/70/80 mt-4 leading-relaxed">Certain FIVE LENS training programmes and workshops are accredited to provide Continuing Coach Education Units (CCEUs). This allows you to integrate our powerful behavioural framework into your practice while simultaneously earning the credits you need to maintain your professional coaching standing.</p>
          </details>
        </div>
      </section>


    </main>
  );
}
